import {
  memo,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type FC,
  type MouseEvent as ReactMouseEvent,
} from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { HexColorPicker } from 'react-colorful'
import {
  EDITOR_CONTENT_ROLE_VALUES,
  EDITOR_FONT_FAMILIES,
  clampEditorAuthoredPt,
  clampEditorStrokeWidthPt,
  cssColorToHex,
  editorTextBoxStyleActions,
  editorTextBoxRoleLabel,
  editorTextBoxSwatchRows,
  formatPt,
  isEditorCaret,
  removeColorTarget,
} from '@/utils/editor-text-box/editorTextBoxAdapter'
import type {
  EditorCaseData,
  EditorContentRoleValue,
  EditorPosition,
  EditorSelection,
  EditorStylingAction,
  EditorTextBoxMount,
  EditorTextBoxThemeColors,
} from '@/utils/editor-text-box/editorTextBoxAdapter'
import Icon from 'components/priority/Icon/Icon'
import { familyAllowsBold } from '@/utils/styling/fontIntegrity'
import ColorTileGrid from './ColorTileGrid'
import { EditorTextBoxColorButton } from './EditorTextBoxColorButton'
import { EDITOR_COLOR_TILE_GRID_WIDTH_PX } from './EditorTextBoxToolbar.geometry'
import { EditorTextBoxFieldMenu } from './EditorTextBoxFieldMenu'
import {
  EDITOR_SIZE_STEP_HOLD_MS,
  EDITOR_SIZE_STEP_PT,
  EDITOR_SIZE_STEP_REPEAT_MS,
} from './editorTextBoxSizeStep'
import {
  FloatingPopover,
  POPOVER_GAP_PX,
  useFloatingPopoverPlacement,
} from './EditorTextBoxFloatingPopover'
import { EDITOR_TEXT_BOX_ROLE_MENU_IN_TOOLBAR } from './EditorTextBoxToolbar.flags'
import {
  EditorTextBoxAlignMenu,
  paintedAlignAtCaret,
  paintedVerticalAlign,
  type TextAlignKind,
  type TextVerticalAlignKind,
} from './EditorTextBoxAlignMenu'
import EditorTextBoxFontMenu, { MenuTick } from './EditorTextBoxFontMenu'
import { EditorTextBoxShadowMenu } from './EditorTextBoxShadowMenu'
import useNestedChromeDismiss from './useNestedChromeDismiss'
import * as S from './EditorTextBoxToolbar.styled'

type MenuTriggerProps = {
  /** The trigger's `data-editor-text-*` hook. */
  attrs: Record<string, true>
  title: string
  open: boolean
  label: string
  onMouseDown: (e: ReactMouseEvent<HTMLElement>) => void
}

/**
 * A list popover's trigger: a bordered field like the font one, sized to its
 * label, with a caret that flips while the popover is open. It keeps this
 * form on every canvas; a canvas too narrow for the row wraps the toolbar.
 */
const MenuTrigger: FC<MenuTriggerProps> = ({
  attrs,
  title,
  open,
  label,
  onMouseDown,
}) => (
  <S.Field
    type="button"
    {...attrs}
    title={title}
    $fit
    $active={open}
    onMouseDown={onMouseDown}
  >
    <S.FieldLabel>{label}</S.FieldLabel>
    <S.Caret $up={open}>
      <Icon icon="chevronDown" width="11px" height="6px" />
    </S.Caret>
  </S.Field>
)

type SelectionRects = ReturnType<EditorTextBoxMount['selectionRects']>

type ToggleStates = ReturnType<EditorTextBoxMount['toggleStates']>
type TriToggleStates = ReturnType<EditorTextBoxMount['toggleTriStates']>
type TriState = TriToggleStates['bold']

type MenuName =
  | 'align'
  | 'case'
  | 'color'
  | 'field'
  | 'font'
  | 'linespacing'
  | 'outline'
  | 'role'
  | 'shadow'
  | 'spacing'

const FONT_FAMILIES = EDITOR_FONT_FAMILIES

const TOGGLES_OFF: ToggleStates = {
  bold: false,
  italic: false,
  underline: false,
  strikethrough: false,
}

const TRI_OFF: TriToggleStates = {
  bold: 'off',
  italic: 'off',
  underline: 'off',
  strikethrough: 'off',
}

const ariaPressed = (tri: TriState) =>
  tri === 'mixed' ? 'mixed' : (String(tri === 'on') as 'true' | 'false')

const SIZE_FIELD_EMPTY = '—'
const SIZE_FIELD_MIXED = 'Mixed'

type TextCaseKind = 'none' | 'uppercase' | 'lowercase' | 'capitalize'

/** A tile's tooltip: `editor.textBox.toolbar.<titleKey>`, the English `title` its fallback. */
export type EditorTextBoxTileTitle = { titleKey: string; title: string }

export const CASE_TILES: ReadonlyArray<
  EditorTextBoxTileTitle & {
    kind: TextCaseKind
    icon:
      | 'letterCaseIcon'
      | 'letterCaseTitleIcon'
      | 'letterCaseLowerIcon'
      | 'eraserIcon'
  }
> = [
  {
    kind: 'uppercase',
    icon: 'letterCaseIcon',
    titleKey: 'uppercase',
    title: 'UPPERCASE',
  },
  {
    kind: 'capitalize',
    icon: 'letterCaseTitleIcon',
    titleKey: 'capitalize',
    title: 'Capitalize',
  },
  {
    kind: 'lowercase',
    icon: 'letterCaseLowerIcon',
    titleKey: 'lowercase',
    title: 'lowercase',
  },
  {
    kind: 'none',
    icon: 'eraserIcon',
    titleKey: 'removeCase',
    title: 'Remove case',
  },
]

/** The tooltip of a case or alignment tile in the current language. */
export const tileTitle = (
  t: ReturnType<typeof useTranslation>['t'],
  tile: EditorTextBoxTileTitle,
): string =>
  String(
    t(`editor.textBox.toolbar.${tile.titleKey}`, { defaultValue: tile.title }),
  )

/**
 * To the right of the row, top-aligned; flipped left at the window's edge.
 * The tiles view is the tile grid's width; the hex view is as wide as its
 * picker and its input row need. `width` is the flyout's own measured
 * width, so the flip is judged on the box that paints; until it is known
 * the flyout stays out of sight.
 */
const outlineFlyoutStyle = (
  row: DOMRect,
  view: 'tiles' | 'hex',
  width: number | null,
): CSSProperties => ({
  width: view === 'tiles' ? EDITOR_COLOR_TILE_GRID_WIDTH_PX : 'max-content',
  left:
    width === null
      ? 4
      : Math.max(
          4,
          row.right + width > window.innerWidth ? row.left - width : row.right,
        ),
  top: Math.max(8, Math.min(row.top, window.innerHeight - 340)),
  visibility: width === null ? 'hidden' : undefined,
})

type EditorTextBoxToolbarProps = {
  getMount: () => EditorTextBoxMount | null
  getHostEl: () => HTMLElement | null
  subscribeRefit?: (onRefit: () => void) => () => void
  onHoldSelection?: (rects: SelectionRects | null) => void
  themeColors?: EditorTextBoxThemeColors
  onPulseRoleTarget?: (rects: SelectionRects) => void
  onConfigureField?: (
    fieldKey: string,
    selection: EditorSelection | null,
    serviceId?: string,
    rowType?: string,
  ) => void
  isAdmin?: boolean
  caseData?: EditorCaseData
  onAuthorRole?: (
    role: EditorContentRoleValue,
    selection: EditorSelection | null,
  ) => void
  scanUsedFonts?: () => string[]
  /** Inserts a horizontal rule inside the box at the caret. */
  onInsertRule?: () => void
}

const EditorTextBoxToolbar: FC<EditorTextBoxToolbarProps> = ({
  getMount,
  getHostEl,
  subscribeRefit,
  onHoldSelection,
  themeColors,
  onPulseRoleTarget,
  onConfigureField,
  isAdmin,
  caseData,
  onAuthorRole,
  scanUsedFonts,
  onInsertRule,
}) => {
  const { t } = useTranslation()
  const [openMenu, setOpenMenu] = useState<MenuName | null>(null)
  const [usedFonts, setUsedFonts] = useState<string[]>([])
  const [openFieldGroups, setOpenFieldGroups] = useState<Set<string>>(
    () => new Set(),
  )
  const [hex, setHex] = useState('#000000')
  // The colour popovers open on their tiles; the palette tile reveals the
  // hex view, and every open starts on the tiles again. Both views stay
  // mounted and toggle `hidden`: a tile that unmounted itself on click would
  // reach the document's outside-click guard as a detached target, and the
  // guard would read that as a press outside the canvas.
  const [colorView, setColorView] = useState<'tiles' | 'hex'>('tiles')
  const [outlineHex, setOutlineHex] = useState('#000000')
  const [letterSpacingLabel, setLetterSpacingLabel] = useState('')
  const [lineSpacingLabel, setLineSpacingLabel] = useState('')
  const [outlineWidthLabel, setOutlineWidthLabel] = useState('0')
  const [outlineWidthDraft, setOutlineWidthDraft] = useState<string | null>(
    null,
  )
  // The "Outline color ›" row's flyout: the row's rect places it, and it
  // opens on its own tiles like the colour popovers.
  const [outlineFlyout, setOutlineFlyout] = useState<DOMRect | null>(null)
  const [outlineView, setOutlineView] = useState<'tiles' | 'hex'>('tiles')
  const outlineFlyoutRef = useRef<HTMLDivElement | null>(null)
  const [outlineFlyoutWidth, setOutlineFlyoutWidth] = useState<number | null>(
    null,
  )
  const outlineFlyoutTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [letterSpacingDraft, setLetterSpacingDraft] = useState<string | null>(
    null,
  )
  const [lineSpacingDraft, setLineSpacingDraft] = useState<string | null>(null)
  const [fontSizeLabel, setFontSizeLabel] = useState(SIZE_FIELD_EMPTY)
  // The box's maximum text size, when it is what holds the size at the
  // caret rather than the size the person authored. Under Fill text with a
  // maximum the two differ by design — the row keeps what was typed, the
  // field shows what is painted — so the field says which it is showing.
  const [atMaxPt, setAtMaxPt] = useState<number | null>(null)
  const [fontFamilyLabel, setFontFamilyLabel] = useState('')
  const [sizeDraft, setSizeDraft] = useState<string | null>(null)
  const sizeDirtyRef = useRef(false)
  const sizeCancelRef = useRef(false)
  const [toggles, setToggles] = useState(TOGGLES_OFF)
  const [triToggles, setTriToggles] = useState(TRI_OFF)
  // What the colour button's tooltip names: the selection's colour, or mixed.
  const [colorAtSelection, setColorAtSelection] = useState<string | null>(null)
  const [colorMixed, setColorMixed] = useState(false)
  const [boldAvailable, setBoldAvailable] = useState(true)
  const [activeCase, setActiveCase] = useState<TextCaseKind>('none')
  const [activeAlign, setActiveAlign] = useState<TextAlignKind | null>(null)
  const [activeVerticalAlign, setActiveVerticalAlign] =
    useState<TextVerticalAlignKind | null>(null)
  const [activeRole, setActiveRole] = useState<EditorContentRoleValue | null>(
    null,
  )
  // The open popover hangs under the toolbar at its trigger's left edge.
  const menuTriggerLeftRef = useRef(0)
  const popoverRef = useRef<HTMLDivElement | null>(null)
  const savedSelRef = useRef<EditorSelection | null>(null)
  // The strip spans the canvas; the toolbar centres in it and wraps to a
  // second row on a canvas too narrow for one. The inline toolbar (no
  // canvas) is as wide as its content and never wraps.
  const [strip, setStrip] = useState<{ left: number; width: number } | null>(
    null,
  )

  useLayoutEffect(() => {
    const canvasEl = document.querySelector<HTMLElement>(
      '[data-editor-ui="editor-canvas"]',
    )
    if (!canvasEl) return undefined
    const update = () => {
      const row = canvasEl.offsetParent?.getBoundingClientRect()
      const c = canvasEl.getBoundingClientRect()
      const next = { left: c.left - (row?.left ?? 0), width: c.width }
      setStrip((prev) =>
        prev && prev.left === next.left && prev.width === next.width
          ? prev
          : next,
      )
    }
    update()
    const ro =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null
    ro?.observe(canvasEl)
    window.addEventListener('resize', update)
    return () => {
      ro?.disconnect()
      window.removeEventListener('resize', update)
    }
  }, [])

  const refreshFromSelection = () => {
    const next = getMount()?.toggleStates?.() ?? TOGGLES_OFF
    setToggles((prev) =>
      prev.bold === next.bold &&
      prev.italic === next.italic &&
      prev.underline === next.underline &&
      prev.strikethrough === next.strikethrough
        ? prev
        : next,
    )
    const nextTri = getMount()?.toggleTriStates?.() ?? TRI_OFF
    setTriToggles((prev) =>
      prev.bold === nextTri.bold &&
      prev.italic === nextTri.italic &&
      prev.underline === nextTri.underline &&
      prev.strikethrough === nextTri.strikethrough
        ? prev
        : nextTri,
    )
    const mount = getMount()
    const readout = mount?.fontSizeReadout?.() ?? { kind: 'none' }
    const authoredPt =
      readout.kind === 'pt' && Number.isFinite(readout.pt) ? readout.pt : null
    setFontSizeLabel(
      readout.kind === 'mixed'
        ? SIZE_FIELD_MIXED
        : authoredPt === null
          ? SIZE_FIELD_EMPTY
          : formatPt(authoredPt),
    )
    const nextAtMax = readout.kind === 'pt' ? (readout.atMax ?? null) : null
    setAtMaxPt((prev) => (prev === nextAtMax ? prev : nextAtMax))
    const color = mount?.colorReadout?.()
    setColorAtSelection(color?.kind === 'color' ? color.color : null)
    setColorMixed(color?.kind === 'color' && color.mixed)
    const pos: EditorPosition | null = getMount()?.getModelPosition?.() ?? null
    const family = pos
      ? (getMount()?.resolvedStyleAtPos?.(pos)?.fontFamily ?? null)
      : null
    setBoldAvailable(familyAllowsBold(family))
    const textCase =
      (pos ? getMount()?.resolvedStyleAtPos?.(pos)?.textCase : null) ?? 'none'
    setActiveCase((prev) => (prev === textCase ? prev : textCase))
    const align = paintedAlignAtCaret(getHostEl())
    setActiveAlign((prev) => (prev === align ? prev : align))
    const verticalAlign = paintedVerticalAlign(getHostEl())
    setActiveVerticalAlign((prev) =>
      prev === verticalAlign ? prev : verticalAlign,
    )
    const effFamily = getMount()?.effectiveFontFamily?.() ?? ''
    setFontFamilyLabel((prev) => (prev === effFamily ? prev : effFamily))
    const ls = getMount()?.effectiveLetterSpacing?.()
    if (typeof ls === 'number' && Number.isFinite(ls))
      setLetterSpacingLabel(String(Math.round(ls * 100) / 100))
    const lh = getMount()?.effectiveLineHeight?.()
    if (typeof lh === 'number' && Number.isFinite(lh))
      setLineSpacingLabel(String(Math.round(lh * 100) / 100))
    const stroke = getMount()?.effectiveTextStrokeWidth?.()
    if (typeof stroke === 'number' && Number.isFinite(stroke))
      setOutlineWidthLabel(stroke === 0 ? '0' : formatPt(stroke))
  }

  useEffect(() => {
    refreshFromSelection()
    document.addEventListener('selectionchange', refreshFromSelection)
    return () =>
      document.removeEventListener('selectionchange', refreshFromSelection)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!subscribeRefit) return undefined
    return subscribeRefit(refreshFromSelection)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscribeRefit])

  const holdSelection = () => {
    const sel = savedSelRef.current
    if (!sel || !onHoldSelection) return
    if (isEditorCaret(sel)) return
    const rects = getMount()?.selectionRects?.(sel.anchor, sel.focus) ?? []
    if (rects.length > 0) onHoldSelection(rects)
  }
  const releaseHeldSelection = () => onHoldSelection?.(null)

  /** Closes whatever nested chrome is open and drops the held selection. */
  const dismissNested = () => {
    setOpenMenu((m) => (m === null ? m : null))
    setOpenFieldGroups((g) => (g.size ? new Set() : g))
    setActiveRole((r) => (r === null ? r : null))
    releaseHeldSelection()
  }

  /**
   * The selection a popover acts on: the live one while the box has focus
   * (the popover's buttons keep focus in the box, so a caret or range the
   * user moved with the keyboard under an open popover is the one they see),
   * the one saved at open once focus has moved into a popover input.
   */
  const selectionForMenu = (): EditorSelection | null => {
    const host = getHostEl()
    const active = document.activeElement
    const boxHasFocus =
      host !== null &&
      active !== null &&
      (active === host || host.contains(active))
    return boxHasFocus
      ? (getMount()?.getModelSelection() ?? savedSelRef.current)
      : savedSelRef.current
  }

  const restoreSelection = () => {
    const sel = selectionForMenu()
    getHostEl()?.focus()
    if (sel) getMount()?.placeRangeAtPos(sel.anchor, sel.focus)
  }

  useNestedChromeDismiss(openMenu !== null, {
    close: dismissNested,
    restore: restoreSelection,
  })

  const guardMouseDown = (e: ReactMouseEvent<HTMLElement>) => {
    if ((e.target as Element).tagName !== 'INPUT') e.preventDefault()
  }

  /**
   * A style applied from the toolbar re-asserts the saved range 80ms later
   * if the selection has collapsed meanwhile: closing chrome and moving focus
   * can drop the highlight right after the write. Only that churn is undone:
   * a key or a press inside the box, or a caret the toolbar places itself,
   * cancels the pending reassert, so a deliberate move is never reverted.
   */
  const reassertSelRef = useRef<EditorSelection | null>(null)
  const cancelSelectionReassert = () => {
    reassertSelRef.current = null
  }
  useEffect(() => {
    const onUserInput = (e: Event) => {
      if (!reassertSelRef.current) return
      const host = getHostEl()
      const t = e.target
      if (host && t instanceof Node && host.contains(t))
        cancelSelectionReassert()
    }
    document.addEventListener('keydown', onUserInput, true)
    document.addEventListener('mousedown', onUserInput, true)
    return () => {
      document.removeEventListener('keydown', onUserInput, true)
      document.removeEventListener('mousedown', onUserInput, true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const scheduleSelectionReassert = (saved: EditorSelection | null) => {
    if (!saved || isEditorCaret(saved)) return
    reassertSelRef.current = saved
    setTimeout(() => {
      const pending = reassertSelRef.current
      if (pending !== saved) return
      reassertSelRef.current = null
      if (
        document.activeElement?.closest?.(
          '[data-editor-text-toolbar], [data-editor-text-name-modal]',
        )
      )
        return
      const live = getMount()?.getModelSelection()
      if (!live || isEditorCaret(live)) {
        getMount()?.placeRangeAtPos(saved.anchor, saved.focus)
        refreshFromSelection()
      }
    }, 80)
  }

  /** A control inside an open popover: acts on the selection, keeps the popover. */
  const directInPopover =
    (run: () => void) => (e: ReactMouseEvent<HTMLElement>) => {
      e.preventDefault()
      const saved = getMount()?.getModelSelection() ?? null
      run()
      refreshFromSelection()
      scheduleSelectionReassert(saved)
    }

  /** A toolbar-level control: dismisses any open popover, then acts. */
  const direct = (run: () => void) => (e: ReactMouseEvent<HTMLElement>) => {
    dismissNested()
    directInPopover(run)(e)
  }

  const selectAllGuarded = (e: ReactMouseEvent<HTMLElement>) => {
    e.preventDefault()
    dismissNested()
    getHostEl()?.focus()
    getMount()?.selectAll()
    refreshFromSelection()
  }

  const toolbarRef = useRef<HTMLDivElement | null>(null)

  const anchorMenuToTrigger = (e: ReactMouseEvent<HTMLElement>) => {
    menuTriggerLeftRef.current = Math.round(
      e.currentTarget.getBoundingClientRect().left,
    )
  }

  // Under the toolbar's bottom edge (the toolbar wraps, so its own edge and
  // not the trigger's), at the trigger's left; the toolbar is observed so a
  // wrap while a popover is open moves the popover with it.
  const popoverStyle = useFloatingPopoverPlacement(
    openMenu,
    popoverRef,
    () => {
      const bar = toolbarRef.current?.getBoundingClientRect()
      if (!bar) return null
      return {
        left: menuTriggerLeftRef.current,
        top: bar.bottom + POPOVER_GAP_PX,
        flipTop: bar.top,
      }
    },
    toolbarRef,
  )

  const toggleMenu = (menu: MenuName) => (e: ReactMouseEvent<HTMLElement>) => {
    e.preventDefault()
    const closing = openMenu === menu
    if (!closing) {
      anchorMenuToTrigger(e)
      savedSelRef.current = getMount()?.getModelSelection() ?? null
      holdSelection()
      if (menu === 'font') setUsedFonts(scanUsedFonts?.() ?? [])
      if (menu === 'field') setOpenFieldGroups(new Set())
      if (menu === 'color') {
        setColorView('tiles')
        seedHexFromSelection()
      }
      if (menu === 'outline') {
        setOutlineFlyout(null)
        setOutlineView('tiles')
      }
      if (menu === 'role') {
        setActiveRole(getMount()?.paragraphRole?.() ?? null)
        // A role is a paragraph's: the pulse shows the whole paragraph(s)
        // the pick will tag, not the word at the caret.
        const m = getMount()
        const target = m?.paragraphRange?.()
        if (target && onPulseRoleTarget) {
          const rects = m?.selectionRects?.(target.anchor, target.focus) ?? []
          if (rects.length > 0) onPulseRoleTarget(rects)
        }
      }
    }
    setOpenMenu((m) => (m === menu ? null : menu))
    if (closing) {
      releaseHeldSelection()
      restoreSelection()
    }
  }

  const toggleGuarded = (_key: string, runFn: () => void) => direct(runFn)

  const stepFontSizeGuarded = (delta: number) =>
    direct(() => stepFontSize(delta))

  // A held stepper button keeps stepping: one step on the press, as a click
  // gives, then after a short hold one step every repeat interval until the
  // button is released or the pointer leaves it, so a size far from the one
  // wanted is reached by holding rather than by clicking as many times as
  // the distance in half points. The document hears the release too, since
  // the pointer can come up anywhere.
  const heldStepRef = useRef<{
    delay: ReturnType<typeof setTimeout> | null
    repeat: ReturnType<typeof setInterval> | null
  }>({ delay: null, repeat: null })
  const stopHeldStep = () => {
    const held = heldStepRef.current
    if (held.delay !== null) clearTimeout(held.delay)
    if (held.repeat !== null) clearInterval(held.repeat)
    held.delay = null
    held.repeat = null
  }
  useEffect(() => {
    document.addEventListener('mouseup', stopHeldStep)
    return () => {
      document.removeEventListener('mouseup', stopHeldStep)
      stopHeldStep()
    }
  }, [])
  const stepFontSizeHeld =
    (delta: number) => (e: ReactMouseEvent<HTMLElement>) => {
      stepFontSizeGuarded(delta)(e)
      stopHeldStep()
      heldStepRef.current.delay = setTimeout(() => {
        heldStepRef.current.repeat = setInterval(() => {
          stepFontSize(delta)
          refreshFromSelection()
        }, EDITOR_SIZE_STEP_REPEAT_MS)
      }, EDITOR_SIZE_STEP_HOLD_MS)
    }

  /**
   * After a pick that keeps its popover open: the live selection is the one
   * the next pick styles and a later dismissal restores, the toolbar re-reads
   * it so the popover's pressed states and the trigger follow the pick, and
   * the held selection is repainted from the box as it now renders.
   */
  const rememberSelectionForMenu = () => {
    savedSelRef.current = getMount()?.getModelSelection() ?? savedSelRef.current
    refreshFromSelection()
    releaseHeldSelection()
    holdSelection()
  }

  /**
   * A pick inside an open popover styles the saved selection and keeps the
   * popover open, so the next pick can follow it and the user compares by
   * eye. Only a press outside, Escape, or a toolbar control closes it.
   */
  const applyFromMenu = (action: EditorStylingAction) => {
    restoreSelection()
    getMount()?.applyStyle(action)
    if (openMenu === 'font') setUsedFonts(scanUsedFonts?.() ?? [])
    rememberSelectionForMenu()
    scheduleSelectionReassert(savedSelRef.current)
  }

  /**
   * Tag the paragraph(s) under the selection with a role; the theme's
   * decoration for that role dresses them through the cascade, so nothing
   * is written onto the runs.
   */
  const pickRole = (role: EditorContentRoleValue | undefined) => {
    restoreSelection()
    getMount()?.setParagraphRole(role)
    setActiveRole(role ?? null)
    rememberSelectionForMenu()
    scheduleSelectionReassert(savedSelRef.current)
  }

  const stepFontSize = (delta: number) => {
    getMount()?.stepFontSize?.(delta)
  }

  /**
   * The hex view opens on the colour the selection paints; a colour a hex
   * input cannot show (transparent, a named colour) keeps the last one applied.
   */
  const seedHexFromSelection = () => {
    const color = getMount()?.colorReadout?.()
    const seeded = color?.kind === 'color' ? cssColorToHex(color.color) : null
    if (seeded) setHex(seeded)
  }

  const commitSize = ({ refocus }: { refocus: boolean }) => {
    releaseHeldSelection()
    const draft = sizeDraft
    setSizeDraft(null)
    const cancelled = sizeCancelRef.current
    sizeCancelRef.current = false
    const dirty = sizeDirtyRef.current
    sizeDirtyRef.current = false
    const pt = Number(draft)
    const valid = !cancelled && dirty && Number.isFinite(pt) && pt > 0
    if (!valid) {
      if (refocus) restoreSelection()
      return
    }
    const sel = savedSelRef.current
    const savedIsRange = sel !== null && !isEditorCaret(sel)
    if (refocus) restoreSelection()
    else if (savedIsRange) getMount()?.placeRangeAtPos(sel.anchor, sel.focus)
    const clampedPt = clampEditorAuthoredPt(pt)
    getMount()?.setFontSize?.(clampedPt, { collapsed: 'arm' })
    refreshFromSelection()
    if (savedIsRange) scheduleSelectionReassert(sel)
  }

  const stepLineSpacing = (delta: number) =>
    directInPopover(() => {
      const cur = getMount()?.effectiveLineHeight?.() ?? 1
      const next = Math.max(0.1, Math.round((cur + delta) * 100) / 100)
      getMount()?.applyStyle(editorTextBoxStyleActions.lineSpacing(next))
    })
  const stepLetterSpacing = (delta: number) =>
    directInPopover(() => {
      getMount()?.stepLetterSpacing?.(delta)
    })
  const commitLineSpacing = () => {
    releaseHeldSelection()
    const draft = lineSpacingDraft
    setLineSpacingDraft(null)
    restoreSelection()
    const v = Number(draft)
    if (Number.isFinite(v) && v > 0) {
      getMount()?.applyStyle(
        editorTextBoxStyleActions.lineSpacing(Math.round(v * 100) / 100),
      )
      refreshFromSelection()
      scheduleSelectionReassert(savedSelRef.current)
    }
  }
  const commitLetterSpacing = () => {
    releaseHeldSelection()
    const draft = letterSpacingDraft
    setLetterSpacingDraft(null)
    restoreSelection()
    const v = Number(draft)
    if (Number.isFinite(v)) {
      getMount()?.applyStyle(
        editorTextBoxStyleActions.letterSpacing(Math.round(v * 100) / 100),
      )
      refreshFromSelection()
      scheduleSelectionReassert(savedSelRef.current)
    }
  }

  /**
   * The outline width is only ever set here; 0 removes the outline. A first
   * step up from 0 paints with the outline colour too, so the stroke shows
   * on text that never had one; every later step sets the width alone.
   */
  const outlineWidthAction = (next: number): EditorStylingAction =>
    (getMount()?.effectiveTextStrokeWidth?.() ?? 0) === 0 && next > 0
      ? editorTextBoxStyleActions.textOutline(next, outlineHex.trim())
      : editorTextBoxStyleActions.textStrokeWidth(next)
  const stepOutlineWidth = (delta: number) =>
    directInPopover(() => {
      const cur = getMount()?.effectiveTextStrokeWidth?.() ?? 0
      const next = clampEditorStrokeWidthPt(cur + delta)
      getMount()?.applyStyle(outlineWidthAction(next))
    })
  const commitOutlineWidth = () => {
    releaseHeldSelection()
    const draft = outlineWidthDraft
    setOutlineWidthDraft(null)
    restoreSelection()
    const v = Number(draft)
    if (Number.isFinite(v) && v >= 0) {
      getMount()?.applyStyle(outlineWidthAction(clampEditorStrokeWidthPt(v)))
      refreshFromSelection()
      scheduleSelectionReassert(savedSelRef.current)
    }
  }

  const clearOutlineFlyoutTimer = () => {
    if (outlineFlyoutTimer.current) {
      clearTimeout(outlineFlyoutTimer.current)
      outlineFlyoutTimer.current = null
    }
  }
  const openOutlineFlyout = (row: HTMLElement) => {
    clearOutlineFlyoutTimer()
    setOutlineFlyout(row.getBoundingClientRect())
  }
  const scheduleOutlineFlyoutClose = () => {
    clearOutlineFlyoutTimer()
    outlineFlyoutTimer.current = setTimeout(() => setOutlineFlyout(null), 160)
  }
  useEffect(() => clearOutlineFlyoutTimer, [])
  // The flyout's own width, read once it is in the DOM and again when its
  // view changes, so it is placed with the width it paints at.
  useLayoutEffect(() => {
    setOutlineFlyoutWidth(
      outlineFlyout
        ? (outlineFlyoutRef.current?.getBoundingClientRect().width ?? null)
        : null,
    )
  }, [outlineFlyout, outlineView])

  /**
   * Runs a field menu insert at the caret or over the selection the user
   * sees, then closes the menu; the caret stays in the box, after what was
   * inserted.
   */
  const insertThroughMount = (insert: (mount: EditorTextBoxMount) => void) => {
    cancelSelectionReassert()
    restoreSelection()
    const mount = getMount()
    if (mount) insert(mount)
    dismissNested()
  }

  const toggleFieldGroup = (group: string) =>
    setOpenFieldGroups((prev) => {
      const next = new Set(prev)
      if (next.has(group)) next.delete(group)
      else next.add(group)
      return next
    })

  const openFieldConfigure =
    (fieldKey: string, serviceId?: string, rowType?: string) =>
    (e: ReactMouseEvent<HTMLElement>) => {
      e.preventDefault()
      releaseHeldSelection()
      setOpenMenu(null)
      onConfigureField?.(fieldKey, savedSelRef.current, serviceId, rowType)
    }

  const portalTarget =
    typeof document !== 'undefined'
      ? document.querySelector('[data-editor-ui="editor-canvas"]')
      : null

  const toolbar = (
    <S.Toolbar
      ref={toolbarRef}
      data-editor-text-toolbar
      $inline={!portalTarget}
      onMouseDown={guardMouseDown}
    >
      <S.Field
        type="button"
        data-editor-text-font
        title={t('editor.textBox.toolbar.fontFamily')}
        $active={openMenu === 'font'}
        onMouseDown={toggleMenu('font')}
      >
        <S.FieldLabel
          style={
            fontFamilyLabel ? { fontFamily: `'${fontFamilyLabel}'` } : undefined
          }
        >
          {fontFamilyLabel || t('editor.textBox.toolbar.font')}
        </S.FieldLabel>
        <S.Caret $up={openMenu === 'font'}>
          <Icon icon="chevronDown" width="11px" height="6px" />
        </S.Caret>
      </S.Field>
      <S.FieldGroup data-editor-text-size-stepper>
        <S.Btn
          type="button"
          data-editor-text-size-dec
          title={t('editor.textBox.toolbar.decreaseFontSize')}
          onMouseDown={stepFontSizeHeld(-EDITOR_SIZE_STEP_PT)}
          onMouseUp={stopHeldStep}
          onMouseLeave={stopHeldStep}
        >
          <Icon icon="minusIcon" width="14px" height="14px" />
        </S.Btn>
        <S.SizeInput
          data-editor-text-size
          title={t('editor.textBox.toolbar.fontSize')}
          inputMode="decimal"
          value={
            sizeDraft ??
            (fontSizeLabel === SIZE_FIELD_MIXED
              ? t('editor.textBox.toolbar.mixed')
              : fontSizeLabel)
          }
          onMouseDown={(e) => {
            if (document.activeElement === e.currentTarget) return
            dismissNested()
            savedSelRef.current = getMount()?.getModelSelection() ?? null
          }}
          onFocus={(e) => {
            sizeDirtyRef.current = false
            sizeCancelRef.current = false
            setSizeDraft(e.currentTarget.value)
            e.currentTarget.select()
            holdSelection()
          }}
          onChange={(e) => {
            sizeDirtyRef.current = true
            setSizeDraft(e.currentTarget.value.replace(/[^0-9.]/g, ''))
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              e.stopPropagation()
              commitSize({ refocus: true })
            } else if (e.key === 'Escape') {
              e.preventDefault()
              e.stopPropagation()
              sizeCancelRef.current = true
              commitSize({ refocus: true })
            } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
              e.preventDefault()
              e.stopPropagation()
              if (fontSizeLabel === SIZE_FIELD_MIXED && sizeDraft === null) {
                stepFontSize(
                  e.key === 'ArrowUp'
                    ? EDITOR_SIZE_STEP_PT
                    : -EDITOR_SIZE_STEP_PT,
                )
                refreshFromSelection()
                return
              }
              const cur = Number(sizeDraft ?? fontSizeLabel)
              const base = Number.isFinite(cur) && cur > 0 ? cur : 24
              const next = Math.min(
                999,
                Math.max(
                  1,
                  base +
                    (e.key === 'ArrowUp'
                      ? EDITOR_SIZE_STEP_PT
                      : -EDITOR_SIZE_STEP_PT),
                ),
              )
              sizeDirtyRef.current = true
              setSizeDraft(String(next))
            }
          }}
          onBlur={() => commitSize({ refocus: false })}
        />
        {atMaxPt !== null && (
          <S.AtMaxBadge
            data-testid="editor-text-size-at-max"
            data-at-max-pt={atMaxPt}
            title={t('editor.textBox.toolbar.atMaxSizeTitle', {
              pt: formatPt(atMaxPt),
            })}
          >
            {t('editor.textBox.toolbar.atMaxSize')}
          </S.AtMaxBadge>
        )}
        <S.Btn
          type="button"
          data-editor-text-size-inc
          title={t('editor.textBox.toolbar.increaseFontSize')}
          onMouseDown={stepFontSizeHeld(EDITOR_SIZE_STEP_PT)}
          onMouseUp={stopHeldStep}
          onMouseLeave={stopHeldStep}
        >
          <Icon icon="plus" width="11px" height="11px" />
        </S.Btn>
      </S.FieldGroup>
      <EditorTextBoxColorButton
        color={colorAtSelection}
        mixed={colorMixed}
        themeColors={themeColors}
        open={openMenu === 'color'}
        onMouseDown={toggleMenu('color')}
      />
      <S.Btn
        type="button"
        data-editor-text-bold
        pressed={toggles.bold}
        aria-pressed={ariaPressed(triToggles.bold)}
        data-editor-text-mixed={triToggles.bold === 'mixed' || undefined}
        aria-disabled={!boldAvailable}
        $disabled={!boldAvailable}
        title={
          boldAvailable
            ? t('editor.textBox.toolbar.bold')
            : t('editor.textBox.toolbar.noBoldStyle')
        }
        onMouseDown={
          boldAvailable
            ? toggleGuarded('bold', () => getMount()?.toggleBold())
            : (e) => e.preventDefault()
        }
      >
        <Icon icon="boldIcon" width="16px" height="16px" />
      </S.Btn>
      <S.Btn
        type="button"
        data-editor-text-italic
        pressed={toggles.italic}
        aria-pressed={ariaPressed(triToggles.italic)}
        data-editor-text-mixed={triToggles.italic === 'mixed' || undefined}
        title={t('editor.textBox.toolbar.italic')}
        onMouseDown={toggleGuarded('italic', () => getMount()?.toggleItalic())}
      >
        <Icon icon="italicIcon" width="16px" height="16px" />
      </S.Btn>
      <S.Btn
        type="button"
        data-editor-text-underline
        pressed={toggles.underline}
        aria-pressed={ariaPressed(triToggles.underline)}
        data-editor-text-mixed={triToggles.underline === 'mixed' || undefined}
        title={t('editor.textBox.toolbar.underline')}
        onMouseDown={toggleGuarded('underline', () =>
          getMount()?.toggleUnderline(),
        )}
      >
        <Icon icon="underlineIcon" width="16px" height="16px" />
      </S.Btn>
      <S.Btn
        type="button"
        data-editor-text-strikethrough
        pressed={toggles.strikethrough}
        aria-pressed={ariaPressed(triToggles.strikethrough)}
        data-editor-text-mixed={
          triToggles.strikethrough === 'mixed' || undefined
        }
        title={t('editor.textBox.toolbar.strikethrough')}
        onMouseDown={toggleGuarded('strikethrough', () =>
          getMount()?.toggleStrikethrough(),
        )}
      >
        <Icon icon="strikethroughIcon" width="16px" height="16px" />
      </S.Btn>
      <S.Btn
        type="button"
        data-editor-text-case
        pressed={openMenu === 'case'}
        title={t('editor.textBox.toolbar.textCase')}
        onMouseDown={toggleMenu('case')}
      >
        <Icon icon="letterCaseIcon" width="16px" height="16px" />
      </S.Btn>
      <S.Sep />
      <S.Btn
        type="button"
        data-editor-text-align
        pressed={openMenu === 'align'}
        title={t('editor.textBox.toolbar.alignment')}
        onMouseDown={toggleMenu('align')}
      >
        <Icon icon="alignLeftBarsIcon" width="16px" height="16px" />
      </S.Btn>
      <S.Btn
        type="button"
        data-editor-text-linespacing
        pressed={openMenu === 'linespacing'}
        title={t('editor.textBox.toolbar.lineSpacing')}
        onMouseDown={toggleMenu('linespacing')}
      >
        <Icon icon="lineSpacingIcon" width="16px" height="16px" />
      </S.Btn>
      <S.Btn
        type="button"
        data-editor-text-spacing
        pressed={openMenu === 'spacing'}
        title={t('editor.textBox.toolbar.letterSpacing')}
        onMouseDown={toggleMenu('spacing')}
      >
        <Icon icon="letterSpacingIcon" width="16px" height="16px" />
      </S.Btn>
      <S.Sep />
      <S.Btn
        type="button"
        data-editor-text-hr
        title={t('editor.textBox.toolbar.insertSeparator')}
        onMouseDown={(e) => {
          e.preventDefault()
          dismissNested()
          onInsertRule?.()
        }}
      >
        <Icon icon="minusIcon" width="16px" height="16px" />
      </S.Btn>
      <S.Btn
        type="button"
        data-editor-text-shadow
        title={t('editor.textBox.toolbar.textShadow')}
        pressed={openMenu === 'shadow'}
        onMouseDown={toggleMenu('shadow')}
      >
        <Icon icon="textShadowIcon" width="16px" height="16px" />
      </S.Btn>
      <S.Btn
        type="button"
        data-editor-text-outline
        title={t('editor.textBox.toolbar.textOutline')}
        pressed={openMenu === 'outline'}
        onMouseDown={toggleMenu('outline')}
      >
        <Icon icon="textOutlineIcon" width="16px" height="16px" />
      </S.Btn>
      <S.Sep />
      <S.Btn
        type="button"
        data-editor-text-undo
        title={t('editor.textBox.toolbar.undo')}
        onMouseDown={direct(() => getMount()?.undo())}
      >
        <Icon icon="undoArrowIcon" width="16px" height="16px" />
      </S.Btn>
      <S.Btn
        type="button"
        data-editor-text-redo
        title={t('editor.textBox.toolbar.redo')}
        onMouseDown={direct(() => getMount()?.redo())}
      >
        <Icon icon="redoArrowIcon" width="16px" height="16px" />
      </S.Btn>
      <S.Sep />
      <S.Btn
        type="button"
        data-editor-text-selectall
        title={t('editor.textBox.toolbar.selectAll')}
        onMouseDown={selectAllGuarded}
      >
        <Icon icon="selectAllIcon" width="16px" height="16px" />
      </S.Btn>
      {EDITOR_TEXT_BOX_ROLE_MENU_IN_TOOLBAR && (
        <MenuTrigger
          attrs={{ 'data-editor-text-role': true }}
          title={t('editor.textBox.toolbar.applyRole')}
          label={t('editor.textBox.toolbar.applyRole')}
          open={openMenu === 'role'}
          onMouseDown={toggleMenu('role')}
        />
      )}
      {/* Last in the toolbar: the field list, the widest popover, hangs from
          its left edge and reaches past the toolbar's end, where the placement
          keeps it inside the window. */}
      <MenuTrigger
        attrs={{ 'data-editor-text-field': true }}
        title={t('editor.textBox.toolbar.insertDetail')}
        label={t('editor.textBox.toolbar.insertDetail')}
        open={openMenu === 'field'}
        onMouseDown={toggleMenu('field')}
      />

      {openMenu === 'color' && (
        <FloatingPopover
          ref={popoverRef}
          style={popoverStyle}
          $menu
          data-editor-text-popover="color"
          onMouseDown={guardMouseDown}
        >
          <ColorTileGrid
            hidden={colorView !== 'tiles'}
            attrPrefix="color"
            swatches={editorTextBoxSwatchRows(themeColors)}
            onPick={(picked) => {
              setHex(picked)
              applyFromMenu(editorTextBoxStyleActions.color(picked))
            }}
            onRemove={() => {
              const restored = removeColorTarget(themeColors)
              setHex(restored)
              applyFromMenu(editorTextBoxStyleActions.color(restored))
            }}
            onCustom={() => setColorView('hex')}
          />
          <S.HexView hidden={colorView !== 'hex'}>
            <S.TextBtn
              type="button"
              data-editor-text-color-tiles-back
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => setColorView('tiles')}
            >
              {t('editor.textBox.toolbar.backToTiles')}
            </S.TextBtn>
            <HexColorPicker color={hex} onChange={setHex} />
            <S.Row>
              <S.TextInput
                data-editor-text-color-input
                value={hex}
                onChange={(e) => setHex(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter')
                    applyFromMenu(editorTextBoxStyleActions.color(hex.trim()))
                }}
              />
              <S.ApplyBtn
                type="button"
                data-editor-text-color-apply
                onMouseDown={(e) => e.preventDefault()}
                onClick={() =>
                  applyFromMenu(editorTextBoxStyleActions.color(hex.trim()))
                }
              >
                {t('editor.textBox.toolbar.apply')}
              </S.ApplyBtn>
            </S.Row>
          </S.HexView>
        </FloatingPopover>
      )}

      {openMenu === 'font' && (
        <FloatingPopover
          ref={popoverRef}
          style={popoverStyle}
          $menu
          data-editor-text-popover="font"
          onMouseDown={guardMouseDown}
        >
          <EditorTextBoxFontMenu
            flyout
            families={FONT_FAMILIES}
            usedFonts={usedFonts}
            currentFamily={fontFamilyLabel || undefined}
            listProps={{ 'data-editor-text-font-list': true }}
            onPick={(family) =>
              applyFromMenu(editorTextBoxStyleActions.fontFamily(family))
            }
          />
        </FloatingPopover>
      )}

      {openMenu === 'align' && (
        <FloatingPopover
          ref={popoverRef}
          style={popoverStyle}
          $pill
          data-editor-text-popover="align"
          onMouseDown={guardMouseDown}
        >
          <EditorTextBoxAlignMenu
            align={activeAlign}
            verticalAlign={activeVerticalAlign}
            titleOf={(tile) => tileTitle(t, tile)}
            onAlign={(align) =>
              applyFromMenu(editorTextBoxStyleActions.align(align))
            }
            onVerticalAlign={(verticalAlign) =>
              applyFromMenu(
                editorTextBoxStyleActions.verticalAlign(verticalAlign),
              )
            }
          />
        </FloatingPopover>
      )}

      {openMenu === 'linespacing' && (
        <FloatingPopover
          ref={popoverRef}
          style={popoverStyle}
          data-editor-text-popover="linespacing"
          onMouseDown={guardMouseDown}
        >
          <S.FieldGroup data-editor-text-linespacing-stepper>
            <S.Btn
              type="button"
              data-editor-text-linespacing-dec
              title={t('editor.textBox.toolbar.decreaseLineSpacing')}
              onMouseDown={stepLineSpacing(-0.1)}
            >
              −
            </S.Btn>
            <S.SizeInput
              data-editor-text-linespacing-input
              title={t('editor.textBox.toolbar.lineSpacing')}
              inputMode="decimal"
              value={lineSpacingDraft ?? lineSpacingLabel}
              onMouseDown={(e) => {
                if (document.activeElement !== e.currentTarget)
                  savedSelRef.current = getMount()?.getModelSelection() ?? null
              }}
              onFocus={(e) => {
                setLineSpacingDraft(e.currentTarget.value)
                e.currentTarget.select()
                holdSelection()
              }}
              onChange={(e) =>
                setLineSpacingDraft(
                  e.currentTarget.value.replace(/[^0-9.]/g, ''),
                )
              }
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  e.stopPropagation()
                  commitLineSpacing()
                } else if (e.key === 'Escape') {
                  e.preventDefault()
                  setLineSpacingDraft(null)
                  releaseHeldSelection()
                  restoreSelection()
                }
              }}
              onBlur={() => {
                setLineSpacingDraft(null)
                releaseHeldSelection()
              }}
            />
            <S.Btn
              type="button"
              data-editor-text-linespacing-inc
              title={t('editor.textBox.toolbar.increaseLineSpacing')}
              onMouseDown={stepLineSpacing(0.1)}
            >
              +
            </S.Btn>
          </S.FieldGroup>
        </FloatingPopover>
      )}

      {openMenu === 'case' && (
        <FloatingPopover
          ref={popoverRef}
          style={popoverStyle}
          $pill
          data-editor-text-popover="case"
          onMouseDown={guardMouseDown}
        >
          <S.Pill data-editor-text-case-list>
            {CASE_TILES.map((tile) => (
              <S.Btn
                type="button"
                key={tile.kind}
                data-case-kind={tile.kind}
                title={tileTitle(t, tile)}
                pressed={activeCase === tile.kind}
                aria-pressed={activeCase === tile.kind}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() =>
                  applyFromMenu(editorTextBoxStyleActions.textCase(tile.kind))
                }
              >
                <Icon icon={tile.icon} width="16px" height="16px" />
              </S.Btn>
            ))}
          </S.Pill>
        </FloatingPopover>
      )}

      {openMenu === 'spacing' && (
        <FloatingPopover
          ref={popoverRef}
          style={popoverStyle}
          data-editor-text-popover="spacing"
          onMouseDown={guardMouseDown}
        >
          <S.FieldGroup data-editor-text-spacing-stepper>
            <S.Btn
              type="button"
              data-editor-text-spacing-dec
              title={t('editor.textBox.toolbar.decreaseLetterSpacing')}
              onMouseDown={stepLetterSpacing(-0.1)}
            >
              −
            </S.Btn>
            <S.SizeInput
              data-editor-text-spacing-input
              title={t('editor.textBox.toolbar.letterSpacingPt')}
              inputMode="decimal"
              value={letterSpacingDraft ?? letterSpacingLabel}
              onMouseDown={(e) => {
                if (document.activeElement !== e.currentTarget)
                  savedSelRef.current = getMount()?.getModelSelection() ?? null
              }}
              onFocus={(e) => {
                setLetterSpacingDraft(e.currentTarget.value)
                e.currentTarget.select()
                holdSelection()
              }}
              onChange={(e) =>
                setLetterSpacingDraft(
                  e.currentTarget.value.replace(/[^0-9.-]/g, ''),
                )
              }
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  e.stopPropagation()
                  commitLetterSpacing()
                } else if (e.key === 'Escape') {
                  e.preventDefault()
                  setLetterSpacingDraft(null)
                  releaseHeldSelection()
                  restoreSelection()
                }
              }}
              onBlur={() => {
                setLetterSpacingDraft(null)
                releaseHeldSelection()
              }}
            />
            <S.Btn
              type="button"
              data-editor-text-spacing-inc
              title={t('editor.textBox.toolbar.increaseLetterSpacing')}
              onMouseDown={stepLetterSpacing(0.1)}
            >
              +
            </S.Btn>
          </S.FieldGroup>
        </FloatingPopover>
      )}

      {openMenu === 'shadow' && (
        <FloatingPopover
          ref={popoverRef}
          style={popoverStyle}
          $menu="scrolling"
          data-editor-text-popover="shadow"
          onMouseDown={guardMouseDown}
        >
          <EditorTextBoxShadowMenu
            readShadow={() => getMount()?.model().textShadow ?? null}
            apply={applyFromMenu}
            swatches={editorTextBoxSwatchRows(themeColors)}
          />
        </FloatingPopover>
      )}

      {openMenu === 'outline' && (
        <FloatingPopover
          ref={popoverRef}
          style={popoverStyle}
          $menu
          data-editor-text-popover="outline"
          onMouseDown={guardMouseDown}
        >
          <S.OutlineMenu>
            <S.StepperRow data-editor-text-outline-stepper>
              <S.Btn
                type="button"
                data-editor-text-outline-dec
                title={t('editor.textBox.toolbar.thinnerOutline')}
                onMouseDown={stepOutlineWidth(-0.5)}
              >
                <Icon icon="minusIcon" width="14px" height="14px" />
              </S.Btn>
              <S.SizeInput
                data-editor-text-outline-input
                title={t('editor.textBox.toolbar.outlineWidthPt')}
                inputMode="decimal"
                value={outlineWidthDraft ?? outlineWidthLabel}
                onMouseDown={(e) => {
                  if (document.activeElement !== e.currentTarget)
                    savedSelRef.current =
                      getMount()?.getModelSelection() ?? null
                }}
                onFocus={(e) => {
                  setOutlineWidthDraft(e.currentTarget.value)
                  e.currentTarget.select()
                  holdSelection()
                }}
                onChange={(e) =>
                  setOutlineWidthDraft(
                    e.currentTarget.value.replace(/[^0-9.]/g, ''),
                  )
                }
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    e.stopPropagation()
                    commitOutlineWidth()
                  } else if (e.key === 'Escape') {
                    e.preventDefault()
                    setOutlineWidthDraft(null)
                    releaseHeldSelection()
                    restoreSelection()
                  }
                }}
                onBlur={() => {
                  setOutlineWidthDraft(null)
                  releaseHeldSelection()
                }}
              />
              <S.Btn
                type="button"
                data-editor-text-outline-inc
                title={t('editor.textBox.toolbar.thickerOutline')}
                onMouseDown={stepOutlineWidth(0.5)}
              >
                <Icon icon="plus" width="14px" height="14px" />
              </S.Btn>
            </S.StepperRow>
            <S.MenuGroupRow
              type="button"
              aria-haspopup="menu"
              aria-expanded={outlineFlyout !== null}
              data-editor-text-outline-color
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={(e) => openOutlineFlyout(e.currentTarget)}
              onMouseLeave={scheduleOutlineFlyoutClose}
              onClick={(e) => openOutlineFlyout(e.currentTarget)}
            >
              {t('editor.textBox.toolbar.outlineColor')}
              <S.MenuGroupChevron>
                <Icon icon="chevronRight" width="10px" height="10px" />
              </S.MenuGroupChevron>
            </S.MenuGroupRow>
          </S.OutlineMenu>
          {outlineFlyout &&
            createPortal(
              <S.Flyout
                ref={outlineFlyoutRef}
                data-editor-text-flyout
                data-editor-text-outline-color-flyout
                $grid
                style={outlineFlyoutStyle(
                  outlineFlyout,
                  outlineView,
                  outlineFlyoutWidth,
                )}
                onMouseDown={guardMouseDown}
                onMouseEnter={clearOutlineFlyoutTimer}
                onMouseLeave={scheduleOutlineFlyoutClose}
              >
                <ColorTileGrid
                  hidden={outlineView !== 'tiles'}
                  attrPrefix="outline"
                  swatches={editorTextBoxSwatchRows(themeColors)}
                  onPick={(picked) => {
                    setOutlineHex(picked)
                    applyFromMenu(
                      editorTextBoxStyleActions.textStrokeColor(picked),
                    )
                  }}
                  onRemove={() =>
                    applyFromMenu(editorTextBoxStyleActions.textOutline(0))
                  }
                  onCustom={() => setOutlineView('hex')}
                />
                <S.HexView hidden={outlineView !== 'hex'}>
                  <S.TextBtn
                    type="button"
                    data-editor-text-outline-tiles-back
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => setOutlineView('tiles')}
                  >
                    {t('editor.textBox.toolbar.backToTiles')}
                  </S.TextBtn>
                  <HexColorPicker color={outlineHex} onChange={setOutlineHex} />
                  <S.Row>
                    <S.TextInput
                      data-editor-text-outline-input-hex
                      value={outlineHex}
                      onChange={(e) => setOutlineHex(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter')
                          applyFromMenu(
                            editorTextBoxStyleActions.textStrokeColor(
                              outlineHex.trim(),
                            ),
                          )
                      }}
                    />
                    <S.ApplyBtn
                      type="button"
                      data-editor-text-outline-apply
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() =>
                        applyFromMenu(
                          editorTextBoxStyleActions.textStrokeColor(
                            outlineHex.trim(),
                          ),
                        )
                      }
                    >
                      {t('editor.textBox.toolbar.apply')}
                    </S.ApplyBtn>
                  </S.Row>
                </S.HexView>
              </S.Flyout>,
              document.body,
            )}
        </FloatingPopover>
      )}

      {EDITOR_TEXT_BOX_ROLE_MENU_IN_TOOLBAR && openMenu === 'role' && (
        <FloatingPopover
          ref={popoverRef}
          style={popoverStyle}
          $menu
          data-editor-text-popover="role"
          onMouseDown={guardMouseDown}
        >
          <S.MenuList
            data-editor-text-role-list
            $width={S.MENU_LIST_WIDTH}
            $canvasBound
          >
            {[...EDITOR_CONTENT_ROLE_VALUES, null].map((role) => {
              const current = activeRole === role
              return (
                <S.MenuItem
                  type="button"
                  key={role ?? 'none'}
                  data-role-kind={role ?? 'none'}
                  {...(current
                    ? { 'data-editor-text-role-current': true }
                    : {})}
                  aria-pressed={current}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pickRole(role ?? undefined)}
                >
                  {role
                    ? editorTextBoxRoleLabel(t, role)
                    : t('editor.textBox.toolbar.noRole')}
                  {current && <MenuTick />}
                </S.MenuItem>
              )
            })}
            {isAdmin && (
              <>
                <S.MenuRule />
                <S.MenuGroupRow
                  type="button"
                  data-editor-text-role-authoring
                  title={t('editor.textBox.toolbar.authorRoleDecorations')}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    // Opens on click, not mousedown: mounting the modal's
                    // body-level backdrop mid-press moves the mouseup off this
                    // row, so the browser retargets the click to <body> and
                    // the outside-click guard deselects the editing box.
                    const captured = getMount()?.getModelSelection() ?? null
                    savedSelRef.current = captured
                    releaseHeldSelection()
                    setOpenMenu(null)
                    onAuthorRole?.(
                      getMount()?.paragraphRole?.() ?? 'heading',
                      captured,
                    )
                  }}
                >
                  {t('editor.textBox.toolbar.roleDecoration')}
                  <S.MenuGroupChevron>
                    <Icon icon="chevronRight" width="10px" height="10px" />
                  </S.MenuGroupChevron>
                </S.MenuGroupRow>
              </>
            )}
          </S.MenuList>
        </FloatingPopover>
      )}

      {openMenu === 'field' && (
        <FloatingPopover
          ref={popoverRef}
          style={popoverStyle}
          $menu
          data-editor-text-popover="field"
          onMouseDown={guardMouseDown}
        >
          <EditorTextBoxFieldMenu
            caseData={caseData}
            openGroups={openFieldGroups}
            onToggleGroup={toggleFieldGroup}
            onInsert={insertThroughMount}
            onConfigure={openFieldConfigure}
          />
        </FloatingPopover>
      )}
    </S.Toolbar>
  )

  return portalTarget
    ? createPortal(
        <S.ToolbarStrip
          data-editor-text-toolbar-strip
          style={strip ? { left: strip.left, width: strip.width } : undefined}
        >
          {toolbar}
        </S.ToolbarStrip>,
        portalTarget,
      )
    : toolbar
}

export default memo(EditorTextBoxToolbar)
